import { TableCell as MuiTableCell, TableRow } from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import type { SortValue } from '../model';
import type { CatalogMessageId } from '~/i18n';

export type PatientListColumnId =
    | 'patient'
    | 'care'
    | 'treatmentStart'
    | 'workflow'
    | 'datasets'
    | 'lastActivity';

/** Design track width in px; 'flex' absorbs the remaining table width. */
type TrackWidth = number | 'flex';

/** A column of a patient table: its header and its design track. */
export type ListTableColumn = {
    /** Header label message id; absent for an unlabelled column. */
    labelId?: CatalogMessageId;
    track: TrackWidth;
    alignEnd?: boolean;
    /** Whether the header names each stage over the rows' stage strips. */
    stageNames?: boolean;
};

export type PatientListColumn = {
    id: PatientListColumnId;
    /** Header label message id; PatientListHeader is the single format site. */
    labelId: CatalogMessageId;
    /** Server-side sort key; only sortable columns carry one. */
    sortKey?: SortValue;
    fullWidth: TrackWidth;
    /** Compact-mode (panel open) track width; absent = column is dropped. */
    compactWidth?: TrackWidth;
    /** Right-aligned column (header and cells). */
    alignEnd?: boolean;
    /** Whether the full column set's header names each stage over the rows' stage strips. */
    stageNames?: boolean;
};

/**
 * Single source of truth for the list's columns: order, header labels, track
 * widths, and compact-mode behavior. `table-layout: fixed` makes the <col>
 * widths (not cell content) drive alignment, so the header and every row stay
 * in lockstep. The flexible Workflow column ('flex') absorbs the remaining
 * width. The patient ID is folded into the Patient cell (not a column) and the
 * Open affordance is a hover overlay (not a column). Compact (panel open)
 * keeps Patient and Workflow, with the assignee and presence folded into
 * them; the panel shows the patient's other facts.
 */
export const PATIENT_LIST_COLUMNS: readonly PatientListColumn[] = [
    {
        id: 'patient',
        labelId: 'patientList.column.patient',
        sortKey: 'name',
        fullWidth: 260,
        compactWidth: 220,
    },
    {
        id: 'care',
        labelId: 'patientList.column.care',
        fullWidth: 190,
    },
    {
        id: 'treatmentStart',
        labelId: 'patientList.column.treatmentStart',
        fullWidth: 120,
    },
    {
        id: 'workflow',
        labelId: 'patientList.column.workflow',
        sortKey: 'courseStage',
        fullWidth: 'flex',
        compactWidth: 'flex',
        stageNames: true,
    },
    {
        id: 'datasets',
        labelId: 'patientList.column.datasets',
        fullWidth: 190,
    },
    {
        id: 'lastActivity',
        labelId: 'patientList.column.lastActivity',
        sortKey: 'lastActivity',
        fullWidth: 150,
        alignEnd: true,
    },
];

const COMPACT_COLUMNS = PATIENT_LIST_COLUMNS.filter(
    (column) => column.compactWidth !== undefined
);

/** The columns the given mode renders, in table order. */
export function visiblePatientListColumns(
    compact: boolean
): readonly PatientListColumn[] {
    return compact ? COMPACT_COLUMNS : PATIENT_LIST_COLUMNS;
}

// Cell padding on the 8px grid; each <col> width folds this in so content equals the design track.
const CELL_PAD_X = 8; // theme.spacing(1)
const OUTER_PAD_LEFT = 21; // 24 minus the 3px selection bar
const OUTER_PAD_RIGHT = 24; // theme.spacing(3)

/** Room for the stage strip's eight marks, 20 px a stage. */
export const STAGE_STRIP_MIN_TRACK = 160;

/**
 * The patient cell plus room for the stage strip's eight marks: 20 px a
 * stage, so the larger current mark clears its neighbours.
 */
export const LIST_MIN_WIDTH_COMPACT = 450;

/** The horizontal cell padding of the column at `index` of `count`. */
function cellPadding(index: number, count: number): number {
    const left = index === 0 ? OUTER_PAD_LEFT : CELL_PAD_X;
    const right = index === count - 1 ? OUTER_PAD_RIGHT : CELL_PAD_X;
    return left + right;
}

function colWidth(
    track: TrackWidth | undefined,
    index: number,
    count: number
): number | undefined {
    return typeof track === 'number'
        ? track + cellPadding(index, count)
        : undefined;
}

/** A table's narrowest width: its fixed tracks, and each 'flex' track at `flexMinTrack`, cell padding folded in. */
export function listMinWidth(
    tracks: readonly TrackWidth[],
    flexMinTrack: number
): number {
    return tracks.reduce<number>(
        (sum, track, index) =>
            sum +
            (track === 'flex' ? flexMinTrack : track) +
            cellPadding(index, tracks.length),
        0
    );
}

/** The full patient list's narrowest width: the Workflow column keeps room for the stage strip. */
export const LIST_MIN_WIDTH_FULL = listMinWidth(
    PATIENT_LIST_COLUMNS.map((column) => column.fullWidth),
    STAGE_STRIP_MIN_TRACK
);

/** <colgroup> pinning each fixed design track, cell padding folded in; the 'flex' tracks share what is left. */
export function ListColgroup({
    tracks,
}: {
    tracks: readonly (TrackWidth | undefined)[];
}) {
    return (
        <colgroup>
            {tracks.map((track, index) => {
                const width = colWidth(track, index, tracks.length);
                return (
                    <col
                        key={index}
                        style={width === undefined ? undefined : { width }}
                    />
                );
            })}
        </colgroup>
    );
}

/** The patient list's <colgroup> for the given mode. */
export function PatientListColgroup({ compact }: { compact: boolean }) {
    return (
        <ListColgroup
            tracks={visiblePatientListColumns(compact).map((column) =>
                compact ? column.compactWidth : column.fullWidth
            )}
        />
    );
}

/** One of the patient list's columns, at its full-mode track, for another table to show. */
export function listTableColumnOf(id: PatientListColumnId): ListTableColumn {
    const column = PATIENT_LIST_COLUMNS.find((each) => each.id === id);
    if (column === undefined) {
        throw new Error(`No patient list column ${id}`);
    }
    return {
        labelId: column.labelId,
        track: column.fullWidth,
        alignEnd: column.alignEnd,
        stageNames: column.stageNames,
    };
}

const cellPaddingX = (theme: Theme) => ({
    padding: theme.spacing(0, 1),
    '&:first-of-type': { paddingLeft: OUTER_PAD_LEFT },
    '&:last-of-type': { paddingRight: theme.spacing(3) },
});

/** Column header cell (sticky via the Table's `stickyHeader`); the theme paints its band. */
export const PatientListHeadCell = styled(MuiTableCell, {
    shouldForwardProp: (prop) => prop !== 'alignEnd',
})<{ alignEnd?: boolean }>(({ theme, alignEnd }) => ({
    ...cellPaddingX(theme),
    ...theme.typography.caption,
    color: theme.palette.text.secondary,
    height: 36,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
    textAlign: alignEnd ? 'right' : 'left',
}));

/**
 * Group-header row cell spanning the whole table. Distinguished from the
 * column header by weight and case, since both are sticky-adjacent and a
 * reader has to tell "what this column is" from "what these rows share".
 */
export const PatientListGroupCell = styled(MuiTableCell)(({ theme }) => ({
    ...cellPaddingX(theme),
    height: 26,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.sunken,
    fontSize: 11,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
}));

/** Patient-row data cell; content fonts/colors are set by the cell children. */
export const PatientListBodyCell = styled(MuiTableCell, {
    shouldForwardProp: (prop) => prop !== 'alignEnd',
})<{ alignEnd?: boolean }>(({ theme, alignEnd }) => ({
    ...cellPaddingX(theme),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    verticalAlign: 'middle',
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: alignEnd ? 'right' : 'left',
}));

/** Height of a patient row, in px, in both column sets. */
const PATIENT_ROW_HEIGHT = 79;

/** Patient-row chassis: hover/selected paint plus the selected accent bar. */
export const PatientTableRow = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
    // Positioning context for the Open hover overlay.
    position: 'relative',
    // One height for both column sets, so opening the panel does not resize a
    // row whose cells fit it. A table row's height is a minimum, so a row
    // whose content needs more, such as tags or a care cell with an
    // assignee, still gets it.
    height: PATIENT_ROW_HEIGHT,
    cursor: 'pointer',
    transition: 'background 100ms',
    backgroundColor: isSelected ? theme.palette.action.selected : 'transparent',
    '&:hover, &:focus-visible': {
        backgroundColor: isSelected
            ? theme.palette.action.selected
            : theme.palette.action.hoverNeutral,
    },
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '-2px',
    },
    // The overlay follows keyboard focus as well as hover, or it would be
    // unreachable without a pointer. Keyboard focus only: closing the panel
    // hands focus back to the row it was opened from, and a mouse close must
    // not leave an Open button standing on a row nobody is pointing at.
    '&:hover .patient-list-open, &:focus-visible .patient-list-open, & .patient-list-open:focus-within':
        {
            opacity: 1,
            pointerEvents: 'auto',
        },
    // The app theme zeroes the last row's cell border; the patient list keeps a
    // divider on every row. Doubled class beats the theme selector specificity.
    '&&:last-of-type > td': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    // 3px accent bar flush to the row's left edge; inset shadow keeps it
    // out of the layout so cell content never shifts.
    '& > td:first-of-type': {
        boxShadow: `inset 3px 0 0 ${
            isSelected ? theme.palette.primary.main : 'transparent'
        }`,
    },
}));
